<template>
    <div class="course-overlay" :class="capsule.is">
        <div v-if="course.test_mode && course.test_mode.intercom && intercom" class="test-mode">
            <div>
                <Logo />
                <Icon name="wrench" size="12" />
                <Txt size="marker">
                    Test mode
                </Txt>
            </div>
        </div>
        <div v-else-if="course.test_mode && course.test_mode.headless" class="test-mode">
            <div>
                <Logo />
                <Icon name="wrench" size="12" />
                <Txt size="marker">
                    Headless test mode
                </Txt>
            </div>
        </div>
        <div v-else-if="course.test_mode && course.test_mode.portal && isTrainingPortal" class="test-mode">
            <div>
                <Logo />
                <Icon name="wrench" size="12" />
                <Txt size="marker">
                    Test mode
                </Txt>
            </div>
        </div>
        <div class="progress-container" :style="{ '--background': backgroundColorRgb }">
            <ProgressBar
                v-for="screen in course.screens"
                :key="screen.id"
                :value="screenProgress(screen)"
                :color="course.theme.colours.standard.primary"
                :style="{
                    '--progress-background-colour': backgroundColorContrastRgb ? `rgba(${backgroundColorContrastRgb}, 0.25)` : null
                }"
                label="Course progress"
                :class="{ active: screen.sequence === selected }"
            />
        </div>
        <nav
            :class="{ hidden: hide && capsule.is.mobile, slideup: isFinishScreen && showBranding }"
            :style="{ '--background': hasPattern || screenType === 'document' || screenType === 'embed' ? null : backgroundColorRgb }"
        >
            <Button
                v-if="collectionKey"
                variant="create"
                :colour="colourClass"
                icon="home"
                aria-label="Go back to collection"
                @click="$emit('home')"
            />
            <Button
                v-if="selected > 0"
                variant="create"
                :colour="colourClass"
                :icon="hasPreviousScreenItem && screenType !== 'caseStudy' && allowQuizBacktrack ? 'chevron-left' : 'arrow-left'"
                class="previous"
                aria-label="Go to previous screen"
                @click="$emit('back')"
            />
            <Button
                v-if="selected !== course.screens.length + 1 && !hasStartButton"
                variant="create"
                :colour="isLocked ? colourClass : course.theme.colours.standard.primary"
                :icon="isLocked ? 'locked' : (hasNextScreenItem ? 'chevron-right' : 'arrow-right')"
                :disabled="isLocked"
                :class="{ 'visually-enabled': isLocked }"
                class="next"
                aria-label="Go to next screen"
                icon-pos="none"
                @click="$emit('next')"
            >
                <hr v-if="(selectedScreenHasScreenItems && selectedScreenItems.length) || selectedScreenHasProgress" />
                <Txt v-if="selectedScreenHasScreenItems && selectedScreenItems.length" size="s">
                    {{ selectedScreenItemSequence }}/{{ selectedScreenItems.length }}
                </Txt>
                <Txt v-else-if="selectedScreenHasProgress" size="s">
                    {{ screenProgress(selectedScreen) || 0 }}%
                </Txt>
            </Button>
            <Button
                v-if="selected === course.screens.length + 1"
                variant="create"
                :colour="colourClass"
                icon="reverse"
                aria-label="Restart course"
                @click="$emit('restart')"
            />
        </nav>
        <UpgradePopup
            v-if="showBranding"
            reason="branding"
            position="top"
            hoverable
            :align="isFinishScreen ? 'center' : 'left'"
            :context="isFinishScreen ? 'remove branding - bottom bar' : 'remove branding - screens'"
            :disabled="!$global.isLoggedIn || headless"
            :class="{ finish: isFinishScreen }"
        >
            <div v-if="isFinishScreen" class="coassemble-branding">
                <template v-if="capsule.is.tablet">
                    <Txt>
                        Discover how easy it is to create your own effective microlearning with <Logo class="full" />
                    </Txt>
                </template>
                <Txt v-else size="s" flex>
                    This course was made with <Logo class="full" />.
                    Discover how easy it is to create your own effective microlearning
                </Txt>
                <Button
                    variant="create"
                    size="xxxs" heading
                    :href="$global.isLoggedIn && preview ? null : '//coassemble.com'"
                    :fill="capsule.is.tablet"
                    slim
                    target="_blank"
                >
                    Try for free
                </Button>
            </div>
            <Button
                v-else
                variant="create"
                colour="white"
                size="marker-s"
                hide-icon
                :href="$global.isLoggedIn && preview ? null : '//coassemble.com'"
                class="coassemble"
                slim
                target="_blank"
            >
                Made with
                <LogoSymbol v-if="capsule.is.mobile" class="symbol" />
                <Logo v-else class="full" />
            </Button>
        </UpgradePopup>
        <div
            v-else-if="course.theme.logo && course.theme.logo.enabled"
            class="logo-wrapper"
            :class="[capsule.is, { box: course.theme.logo.box }]"
        >
            <img
                :src="course.theme.logo.asset"
                :alt="course.theme.logo.alt"
            />
        </div>
    </div>
</template>

<script>
import useLearnerState from '@/store/learner.author';

import { COLOUR_DARK, COLOUR_LIGHT } from '@/store/theme';
import { getItemKeyByType } from '@/helpers/tile';
import { useResponsive } from '@/store/responsive';
import { isCompletable } from '@/helpers/utilities';
import { hexToRgb, readable } from '@/helpers/colour';
import { startScreen, finishScreen } from '@/helpers/course';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Button from '@/components/utility/button';
import UpgradePopup from '@/components/author/upgrade-popup';
import ProgressBar from '@/components/progress/progress-bar';

import Logo from '@/assets/CO_Logo_H_RGB.svg';
import LogoSymbol from '@/assets/CO_Symbol_RGB.svg';

export default {
    name: 'CourseNavigation',
    components: { Txt, Icon, Button, UpgradePopup, ProgressBar, Logo, LogoSymbol },
    inject: ['$global'],
    props: {
        course: { type: Object, default: null },
        selected: { type: Number, default: 0 },

        hide: { type: Boolean, default: false },
        preview: { type: Boolean, default: false },
        headless: { type: Boolean, default: false },
        intercom: { type: Boolean, default: false }
    },
    emits: ['back', 'next', 'restart', 'upgrade', 'home'],
    data() {
        return {
            isTrainingPortal: false,
            capsule: useResponsive('capsule')
        };
    },
    computed: {
        collectionKey() {
            return this.$route.params.collectionKey;
        },
        startScreen() {
            return startScreen(this.course);
        },
        finishScreen() {
            return finishScreen(this.course, !this.$global.isLoggedIn, this.headless);
        },
        lockToScreen() {
            if (!this.course) return -1;
            if (!(this.course.screens && Array.isArray(this.course.screens))) return -1;
            if (this.preview) return -1;
            const index = this.course.screens.findIndex(screen => this.isCompletable(screen) && !screen.completed);
            if (~index) return index;
            return this.course.screens.length;
        },
        lockToScreenSequence() {
            if (!this.course) return 0;
            return this.course.screens[this.lockToScreen]
                && this.course.screens[this.lockToScreen].sequence
                || this.course.screens.length + 2;
        },
        canNavigateForward() {
            if (this.hasNextScreenItem) return true;
            return this.selected < this.lockToScreenSequence;
        },
        selectedScreen() {
            if (this.selected === this.course.screens.length + 1) return this.finishScreen;
            if (this.selected === 0) return this.startScreen;
            return this.course.screens.find(screen => screen.sequence === this.selected);
        },
        selectedScreenItemIndex() {
            return useLearnerState.state.activeItemIndex;
        },
        selectedScreenItemSequence() {
            return Math.min(this.selectedScreenItemIndex + 1, this.selectedScreenItems.length);
        },
        selectedScreenItem() {
            return useLearnerState.getScreenItem(this.selectedScreen.id, this.selectedScreenItemIndex);
        },
        isLocked() {
            if (this.screenType === 'quiz' && !this.selectedScreen.completed && this.selectedScreenItem) {
                return !this.selectedScreenItem.answered;
            }
            return this.selected === this.lockToScreenSequence;
        },
        screenType() {
            return this.selectedScreen
                && this.selectedScreen.type
                && this.selectedScreen.type.name;
        },
        themeType() {
            return this.screenType === 'finish' ? 'cover' : 'standard';
        },
        selectedScreenHasProgress() {
            return this.screenType === 'video'
                && this.selectedScreen.object.images.length > 0
                && this.selectedScreen.object.images[0].enforce_completion;
        },
        selectedScreenHasScreenItems() {
            return ['iaAcronym', 'iaChecklist', 'caseStudy', 'process', 'quiz', 'iaHotspot', 'slideshow'].includes(this.screenType);
        },
        selectedScreenItems() {
            if (!this.selectedScreenHasScreenItems) return [];
            if (this.screenType === 'quiz') return this.selectedScreen.questions;
            if (this.screenType === 'iaChecklist') {
                const items = this.selectedScreen.object[getItemKeyByType(this.screenType)];
                return items.reduce((acc, item) => {
                    if (item.subitems) {
                        return [...acc, item, ...item.subitems];
                    }
                    return [...acc, item];
                }, []);
            }
            return this.selectedScreen.object[getItemKeyByType(this.screenType)];
        },
        hasPreviousScreenItem() {
            if (!this.selectedScreenHasScreenItems) return;
            return this.selectedScreenItemIndex > 0;
        },
        hasNextScreenItem() {
            if (!this.selectedScreenHasScreenItems) return;
            return this.selectedScreenItemIndex < this.selectedScreenItems.length - 1;
        },
        hasPattern() {
            return this.screenType === 'finish'
                && this.selectedScreen.object.display_pattern !== false
                && this.course.theme;
        },
        hasStartButton() {
            return this.selected === 0
                && this.selectedScreen.object.display_button;
        },
        hasBackgroundImage() {
            return this.selectedScreen
                && this.selectedScreen.asset
                && this.selectedScreen.object
                && this.selectedScreen.object.layout === 'background';
        },
        hasBackgroundColour() {
            return this.selectedScreen
                && this.selectedScreen.object
                && this.selectedScreen.object.bg_colour;
        },
        hasDefaultBackground() {
            return ['iaAcronym', 'iaChecklist', 'caseStudy', 'process', 'quiz'].includes(this.screenType);
        },
        hasTheme() {
            return this.course
                && this.course.theme
                && this.course.theme.colours
                && !!this.course.theme.colours[this.themeType];
        },
        backgroundColor() {
            if (this.hasBackgroundImage) return;
            if (this.hasBackgroundColour) return this.selectedScreen.object.bg_colour;
            if (this.hasTheme) return this.course.theme.colours[this.themeType].background;
            if (this.hasDefaultBackground) return '#F6F7F8';
            return null;
        },
        backgroundColorContrast() {
            if (!this.backgroundColor) return;
            return readable(hexToRgb(this.backgroundColor)).contrast;
        },
        backgroundColorContrastRgb() {
            const colour = this.backgroundColorContrast || COLOUR_DARK;
            const [r, g, b] = hexToRgb(colour);
            return `${r}, ${g}, ${b}`;
        },
        backgroundColorRgb() {
            if (!this.backgroundColor) return;
            return hexToRgb(this.backgroundColor).join(', ');
        },
        colourClass() {
            if (this.backgroundColor && this.backgroundColorContrast === COLOUR_LIGHT) return 'translucent';
            if (this.backgroundColor && this.backgroundColorContrast === COLOUR_DARK) return 'translucent-dark';
            if (this.hasBackgroundImage) return 'translucent-dark';
            return null;
        },
        isFinishScreen() {
            return this.selected === this.course.screens.length + 1;
        },
        showBranding() {
            if (this.preview && !this.$global.isLoggedIn) return false;
            return !(this.course.upgraded || this.$global.hasPlan('solo'));
        },
        allowQuizBacktrack() {
            if (this.screenType === 'quiz' && this.selectedScreen.scored) return false;
            return this.selectedScreen.completed;
        }
    },
    mounted() {
        this.boundary = this.$el;
        window.addEventListener('message', this.onMessage);
        document.addEventListener('keyup', this.onKeyPress);
    },
    methods: {
        isCompletable(screen) {
            if (screen.type.name === 'video' && screen.object.images.length > 0 && screen.object.images[0].enforce_completion) return true;
            return isCompletable(screen.type && screen.type.completable);
        },
        onMessage(event) {
            const data = event && event.data;
            if (!data) return;
            if (data === 'trainingportal') this.isTrainingPortal = true;
        },
        onKeyPress(event) {
            if (event.key === 'ArrowRight' && this.canNavigateForward) this.$emit('next');
            if (event.key === 'ArrowLeft') this.$emit('back');
        },
        screenProgress(screen) {
            if (!screen) return 0;
            if (screen.completed) return 100;
            if (screen.sequence < this.selected) return 100;
            if (screen.type.name === 'video') {
                return Math.round(screen.object.images[0].completionPercentage * 100);
            }
            if (this.hasScreenItems(screen)) {
                const items = this.screenItems(screen);
                if (!items) return 0;
                const progress = Math.round((items.filter(item => item.completed).length / items.length) * 100);
                return progress;
            }
            return 0;
        },
        hasScreenItems(screen) {
            return ['iaAcronym', 'caseStudy', 'process', 'quiz', 'iaHotspot', 'slideshow'].includes(screen.type.name);
        },
        screenItems(screen) {
            if (screen.type.name === 'quiz') return screen.questions;
            return screen.object[getItemKeyByType(screen.type.name)];
        }
    }
};
</script>

<style lang="less" scoped>
.course-overlay {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    pointer-events: none;
    user-select: none;
    margin: auto;
    z-index: 1;
    .progress-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1;
        width: var(--full-width);
        padding: 16px;
        background: linear-gradient(0deg, rgba(~"var(--background)", 0.00) 0%, rgba(~"var(--background)", 0.94) 100%);
        display: flex;
        gap: 6px;
        .progress {
            flex: 1;
            height: 4px;
            transition: width 0.35s var(--curve);
            &.active {
                outline: 1px solid var(--progress-background-colour);
                outline-offset: 2px;
            }
        }
    }
    nav {
        pointer-events: none;
        position: absolute;
        left: 0;
        bottom: 0;
        right: 0;
        padding: 24px;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        background: linear-gradient(180deg, rgba(~"var(--background)", 0.00) 0%, rgba(~"var(--background)", 0.94) 100%);
        transition: var(--smooth-fast);
        transform: translateY(0);
        &.hidden {
            transform: translateY(100%);
        }
        .cobutton {
            pointer-events: all;
            flex-direction: row-reverse;
            hr {
                width: 1px;
                height: 20px;
                border: 0;
                background-color: currentColor;
                opacity: 0.4;
                margin: 0 12px;
            }
            &.next {
                min-width: 108px;
                border-radius: 32px;
            }
        }
        &.slideup {
            transform: translateY(-48px);
        }
    }
    .navigation-tip {
        position: absolute;
        bottom: 72px;
        left: 50%;
        transform: translateX(-50%);
        background-color: rgba(var(--coal-rgb), 0.94);
        color: #FFF;
        border-radius: 8px;
        padding: 12px 16px;
        width: 220px;
        z-index: 10;
    }
    .upgrade-popup {
        pointer-events: all;
        position: absolute;
        bottom: 0;
        left: 0;
        margin: 24px;
        &.finish {
            right: 0;
            margin: 0;
        }
        .coassemble-branding {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            background-color: #FFF;
            color: var(--navy);
            border-radius: 24px 24px 0 0;
            height: 48px;
            text-align: center;
            svg.full {
                width: 116px;
                margin-left: 6px;
            }
            .cobutton.create {
                padding: 6px 12px;
            }
        }
    }
    .coassemble.create {
        pointer-events: all;
        padding: 8px 16px;
        box-shadow: var(--shadow-base);
        svg.full {
            width: 96px;
            margin-left: 6px;
        }
        svg.symbol {
            width: 21px;
            margin-left: 3px;
        }
        &.hovering {
            box-shadow: var(--shadow-lg);
        }
    }
    .logo-wrapper {
        position: absolute;
        bottom: 0;
        left: 0;
        margin: 24px;
        max-height: 48px;
        max-width: 132px;
        display: flex;
        align-items: center;
        img {
            max-height: 48px;
            max-width: 132px;
        }
        &.mobile {
            max-height: 32px;
            max-width: 88px;
            img { 
                max-height: 32px;
                max-width: 88px;
            }
        }
        &.box {
            border-radius: 1000px;
            background-color: #FFF;
            padding: 8px 12px;
            box-shadow: var(--shadow-base);
            img {
                max-height: 100%;
                max-width: 100%;
            }
            &.mobile {
                padding: 4px 8px;
            }
        }
    }
    .test-mode {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 999;
        height: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--warning);
        filter: drop-shadow(0px 0px 1px rgba(var(--warning-rgb), 0.04)) drop-shadow(0px 0px 2px rgba(var(--warning-rgb), 0.06)) drop-shadow(0px 2px 8px rgba(var(--warning-rgb), 0.04));
        div {
            position: absolute;
            top: 0;
            display: flex;
            align-items: center;
            border-radius: 0 0 8px 8px;
            padding: 12px 16px;
            background: var(--warning);
            color: var(--navy);
            gap: 4px;
            svg {
                width: 96px;
            }
            &:before, &:after {
                content: '';
                position: absolute;
                top: 8px;
                width: 8px;
                height: 8px;
                background: var(--warning);
                mask-image: radial-gradient(circle 14px at 0 0, transparent 50%, black 50%);
            }
            &:before {
                left: -8px;
                transform: rotate(-90deg);
            }
            &:after {
                right: -8px;
                transform: rotate(180deg);
            }
        }
    }
    &.tablet {
        nav {
            &.slideup {
                transform: translateY(-96px);
            }
        }
        .upgrade-popup.finish {
            margin: 0;
            .coassemble-branding {
                height: auto;
                flex-direction: column;
                padding: 16px;
                gap: 12px;
                svg.full {
                    margin-left: 4px;
                    vertical-align: middle;
                }
            }
        }
    }
    &.mobile {
        .progress-container { padding: 10px 8px; }
        nav {
            padding: 8px;
            &.slideup {
                transform: translateY(-132px);
            }
        }
        .upgrade-popup, .logo-wrapper { margin: 8px; }
    }
}
</style>

<style lang="less">
.course-overlay .test-mode {
    svg {
        path { fill: var(--coal); }
    }
}
</style>
